/**
 * Observability dashboard (Stage-3 P6) — staff-only. Pluggable-panel shell with
 * the Enrichment panel first. Reads /v2/observability/* (server-gated to internal
 * super-admins); this page also gates internally + is hidden from nav (reached at
 * /staff/observability). Tailwind-only.
 */

import { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { getApiErrorMessage } from '@/lib/http';
import {
    observabilityApi,
    type EnrichmentSummary,
    type EnrichmentAttemptRow,
    type ProviderQuota,
    type CalibrationSummary,
    type CalibrationRun,
    type CalibrationContractRow,
} from '@/modules/observability/observability.api';

function isStaffEmail(email?: string | null) {
    const value = String(email || '').toLowerCase();
    return value.endsWith('@ourlanterns.com') || value.endsWith('@careerhackers.io') || value.endsWith('@speakmanity.hk');
}

const WINDOWS = [7, 14, 30, 90];
const pct = (n: number | null | undefined) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);
const usd = (n: number | null | undefined) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const num = (n: number | null | undefined) => (n == null ? '0' : Intl.NumberFormat().format(n));

const OUTCOME_LABEL: Record<string, string> = {
    found: 'Found',
    not_found: 'Not found',
    cache_hit: 'Cache hit',
    skipped_existing: 'Skipped (had email)',
    no_input: 'No input',
    error: 'Error',
};
const STATUS_COLOR: Record<string, string> = {
    deliverable: 'bg-emerald-500',
    risky: 'bg-amber-500',
    undeliverable: 'bg-rose-500',
    unknown: 'bg-gray-400',
};

export function ObservabilityPage() {
    const { ready, user } = useAuth();
    const staff = Boolean(user?.isStaff || isStaffEmail(user?.email));

    const [panel, setPanel] = useState<'enrichment' | 'calibration'>('enrichment');
    const [calibNonce, setCalibNonce] = useState(0);
    const [days, setDays] = useState(7);
    const [summary, setSummary] = useState<EnrichmentSummary | null>(null);
    const [attempts, setAttempts] = useState<EnrichmentAttemptRow[]>([]);
    const [quota, setQuota] = useState<ProviderQuota | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, a, q] = await Promise.all([
                observabilityApi.enrichmentSummary(days),
                observabilityApi.enrichmentAttempts(50),
                observabilityApi.enrichmentQuota(days),
            ]);
            setSummary(s.data);
            setAttempts(a.data.attempts);
            setQuota(q.data);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not load observability data.'));
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        if (ready && staff) void load();
    }, [ready, staff, load]);

    if (ready && !staff) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-500">
                <ShieldCheck size={28} />
                <h1 className="text-lg font-semibold text-gray-800">Staff only</h1>
                <p className="text-sm">This dashboard is available to Lantern internal staff accounts.</p>
            </div>
        );
    }

    return (
        <div className="mx-auto flex h-full w-full max-w-6xl flex-col gap-5 overflow-y-auto p-6">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-900 text-white">
                        <Activity size={18} />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900">Observability</h1>
                        <p className="text-xs text-gray-500">
                            {panel === 'enrichment'
                                ? 'Enrichment · provider health, verification, cache & spend'
                                : 'Search calibration · sourcing yields, delivery-gate rejects, learned contracts'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex overflow-hidden rounded-lg border border-gray-200">
                        {(['enrichment', 'calibration'] as const).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPanel(p)}
                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${panel === p ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                                {p === 'enrichment' ? 'Enrichment' : 'Calibration'}
                            </button>
                        ))}
                    </div>
                    <div className="flex overflow-hidden rounded-lg border border-gray-200">
                        {WINDOWS.map((w) => (
                            <button
                                key={w}
                                onClick={() => setDays(w)}
                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${days === w ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                                {w}d
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => (panel === 'calibration' ? setCalibNonce((n) => n + 1) : void load())}
                        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        disabled={loading && panel === 'enrichment'}
                    >
                        <RefreshCw size={13} className={loading && panel === 'enrichment' ? 'animate-spin' : ''} /> Refresh
                    </button>
                </div>
            </header>

            {error && (
                <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    <AlertTriangle size={15} /> {error}
                </div>
            )}

            {panel === 'calibration' && <CalibrationPanel days={days} nonce={calibNonce} />}

            {panel === 'enrichment' && summary && (
                <>
                    {/* KPI row */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <Kpi label="Attempts" value={num(summary.totals.attempts)} sub={`${num(summary.totals.verifierCalls)} verifications`} />
                        <Kpi label="Resolved rate" value={pct(summary.derived.resolvedRate)} sub={`found ${pct(summary.derived.foundRate)} fresh`} />
                        <Kpi label="Cache hit rate" value={pct(summary.derived.cacheHitRate)} sub={`${num(summary.outcomes.cache_hit || 0)} served`} />
                        <Kpi label="Est. spend" value={usd(summary.spend.estimatedUsd)} sub={`${summary.window.days}d · avg ${summary.totals.avgMs}ms`} />
                    </div>

                    {/* Outcomes + tier-0 + verification */}
                    <div className="grid gap-3 lg:grid-cols-3">
                        <Card title="Outcomes">
                            <div className="flex flex-col gap-1.5">
                                {Object.entries(summary.outcomes).length === 0 && <Empty />}
                                {Object.entries(summary.outcomes)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([k, v]) => (
                                        <Bar key={k} label={OUTCOME_LABEL[k] || k} value={v} max={summary.totals.attempts} />
                                    ))}
                            </div>
                        </Card>
                        <Card title="Tier-0 (Coresignal projected)">
                            <div className="flex h-full flex-col justify-center gap-1">
                                <div className="text-3xl font-semibold text-gray-900">{pct(summary.tier0.hitRate)}</div>
                                <div className="text-xs text-gray-500">
                                    {num(summary.tier0.hits)} hits / {num(summary.tier0.steps)} tier-0 steps — projected emails verified before any paid finder.
                                </div>
                            </div>
                        </Card>
                        <Card title="Verification verdicts">
                            <div className="flex flex-col gap-1.5">
                                {Object.entries(summary.verification).length === 0 && <Empty />}
                                {Object.entries(summary.verification)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([k, v]) => (
                                        <div key={k} className="flex items-center gap-2 text-sm">
                                            <span className={`h-2.5 w-2.5 rounded-full ${STATUS_COLOR[k] || 'bg-gray-400'}`} />
                                            <span className="capitalize text-gray-700">{k}</span>
                                            <span className="ml-auto font-medium text-gray-900">{num(v)}</span>
                                        </div>
                                    ))}
                            </div>
                        </Card>
                    </div>

                    {/* Providers table */}
                    <Card title="Provider performance">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[560px] text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                                        <th className="py-2 pr-3 font-medium">Provider</th>
                                        <th className="py-2 pr-3 text-right font-medium">Hit rate</th>
                                        <th className="py-2 pr-3 text-right font-medium">Found</th>
                                        <th className="py-2 pr-3 text-right font-medium">No match</th>
                                        <th className="py-2 pr-3 text-right font-medium">No key</th>
                                        <th className="py-2 pr-3 text-right font-medium">Error</th>
                                        <th className="py-2 pr-3 text-right font-medium">Avg ms</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.providers.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="py-4 text-center text-gray-400">
                                                No provider steps in this window.
                                            </td>
                                        </tr>
                                    )}
                                    {summary.providers.map((p) => (
                                        <tr key={p.provider} className="border-b border-gray-50 last:border-0">
                                            <td className="py-2 pr-3 font-medium text-gray-800">{p.provider}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-gray-900">{pct(p.hitRate)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-emerald-600">{num(p.found_candidates)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{num(p.no_match)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-gray-400">{num(p.skipped_no_key)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-rose-500">{num(p.error)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{p.avgMs}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    {/* Quota + spend */}
                    <div className="grid gap-3 lg:grid-cols-2">
                        <Card title="Provider quota &amp; spend">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                                            <th className="py-2 pr-3 font-medium">Provider</th>
                                            <th className="py-2 pr-3 text-right font-medium">Calls</th>
                                            <th className="py-2 pr-3 text-right font-medium">Est. $</th>
                                            <th className="py-2 pr-3 text-right font-medium">% quota</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(quota?.providers || []).map((p) => {
                                            const spend = summary.spend.byProvider[p.provider];
                                            return (
                                                <tr key={p.provider} className="border-b border-gray-50 last:border-0">
                                                    <td className="py-2 pr-3">
                                                        <span className="font-medium text-gray-800">{p.provider}</span>
                                                        {!p.configured && <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">no key</span>}
                                                    </td>
                                                    <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{num(p.consumed)}</td>
                                                    <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{usd(spend?.estimatedUsd ?? 0)}</td>
                                                    <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{p.pctUsed == null ? '—' : `${p.pctUsed}%`}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2 text-xs text-gray-500">
                                <span>Total est. spend ({summary.window.days}d)</span>
                                <span className="font-semibold text-gray-800">{usd(summary.spend.estimatedUsd)}</span>
                            </div>
                            {quota?.live?.millionverifier != null && (
                                <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                                    <span>MillionVerifier credits (live)</span>
                                    <span className="font-semibold text-gray-800">{num(quota.live.millionverifier)}</span>
                                </div>
                            )}
                        </Card>

                        <Card title="Daily volume">
                            <div className="flex h-40 items-end gap-1">
                                {summary.timeseries.length === 0 && <Empty />}
                                {summary.timeseries.map((t) => {
                                    const maxAtt = Math.max(1, ...summary.timeseries.map((x) => x.attempts));
                                    return (
                                        <div key={t.day} className="group relative flex flex-1 flex-col items-center justify-end gap-0.5" title={`${t.day}: ${t.attempts} attempts, ${t.found} found`}>
                                            <div className="w-full rounded-t bg-gray-200" style={{ height: `${(t.attempts / maxAtt) * 100}%` }}>
                                                <div className="w-full rounded-t bg-emerald-500" style={{ height: `${t.attempts ? (t.found / t.attempts) * 100 : 0}%` }} />
                                            </div>
                                            <span className="text-[9px] text-gray-400">{t.day.slice(5)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-1 flex items-center gap-3 text-[10px] text-gray-400">
                                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-500" /> found</span>
                                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-gray-200" /> attempts</span>
                            </div>
                        </Card>
                    </div>

                    {/* Recent attempts */}
                    <Card title="Recent attempts">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[640px] text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                                        <th className="py-2 pr-3 font-medium">When</th>
                                        <th className="py-2 pr-3 font-medium">Trigger</th>
                                        <th className="py-2 pr-3 font-medium">Outcome</th>
                                        <th className="py-2 pr-3 font-medium">Finder</th>
                                        <th className="py-2 pr-3 font-medium">Verdict</th>
                                        <th className="py-2 pr-3 text-right font-medium">ms</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attempts.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="py-4 text-center text-gray-400">
                                                No attempts yet.
                                            </td>
                                        </tr>
                                    )}
                                    {attempts.map((r) => (
                                        <tr key={r.id} className="border-b border-gray-50 last:border-0">
                                            <td className="whitespace-nowrap py-2 pr-3 text-gray-500">{new Date(r.at).toLocaleString()}</td>
                                            <td className="py-2 pr-3 text-gray-600">{r.trigger}</td>
                                            <td className="py-2 pr-3">
                                                <span className={`rounded px-1.5 py-0.5 text-xs ${r.outcome === 'found' ? 'bg-emerald-50 text-emerald-700' : r.outcome === 'cache_hit' ? 'bg-sky-50 text-sky-700' : r.outcome === 'error' ? 'bg-rose-50 text-rose-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {OUTCOME_LABEL[r.outcome] || r.outcome}
                                                </span>
                                            </td>
                                            <td className="py-2 pr-3 text-gray-700">{r.finderProvider || '—'}</td>
                                            <td className="py-2 pr-3">
                                                {r.verificationStatus ? (
                                                    <span className="flex items-center gap-1.5 text-gray-700">
                                                        <span className={`h-2 w-2 rounded-full ${STATUS_COLOR[r.verificationStatus] || 'bg-gray-400'}`} /> {r.verificationStatus}
                                                    </span>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{r.ms}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </>
            )}

            {panel === 'enrichment' && loading && !summary && <div className="py-12 text-center text-sm text-gray-400">Loading…</div>}
        </div>
    );
}

const GATE_LABEL: Record<string, string> = {
    delivered: 'Delivered',
    disqualified: 'Auto-reject rule triggered',
    suitability_no: 'Suitability "no"',
    below_floor: 'Below score floor',
    must_pass: 'Must-pass criterion failed',
    required_no: 'Required criterion missing',
};

/** Search Calibration panel (Phase 6) — self-contained; fetches its own data. */
function CalibrationPanel({ days, nonce }: { days: number; nonce: number }) {
    const [summary, setSummary] = useState<CalibrationSummary | null>(null);
    const [runs, setRuns] = useState<CalibrationRun[]>([]);
    const [contracts, setContracts] = useState<CalibrationContractRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        Promise.all([
            observabilityApi.calibrationSummary(days),
            observabilityApi.calibrationRuns(30),
            observabilityApi.calibrationContracts(30),
        ])
            .then(([s, r, c]) => {
                if (!alive) return;
                setSummary(s.data);
                setRuns(r.data.runs);
                setContracts(c.data.contracts);
            })
            .catch((err) => alive && setError(getApiErrorMessage(err, 'Could not load calibration data.')))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [days, nonce]);

    if (loading && !summary) return <div className="py-12 text-center text-sm text-gray-400">Loading…</div>;
    if (error)
        return (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                <AlertTriangle size={15} /> {error}
            </div>
        );
    if (!summary) return null;

    const rejectTotal = Object.values(summary.gateRejects).reduce((a, b) => a + b, 0);
    return (
        <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Kpi label="Runs" value={num(summary.runCount)} sub={`${summary.windowDays}d window`} />
                <Kpi label="Deliver / scored" value={pct(summary.yields.deliverPerScored)} sub={`${num(summary.totals.delivered)} delivered`} />
                <Kpi label="Deliver / scanned" value={pct(summary.yields.deliverPerScanned)} sub={`${num(summary.totals.scanned)} scanned`} />
                <Kpi label="Pre-scan dropped" value={num(summary.totals.preScanDropped)} sub={`${num(summary.totals.scored)} reached the evaluator`} />
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
                <Card title="Delivery-gate outcomes">
                    <div className="flex flex-col gap-1.5">
                        {rejectTotal === 0 && <Empty />}
                        {Object.entries(summary.gateRejects)
                            .filter(([, v]) => v > 0)
                            .sort((a, b) => b[1] - a[1])
                            .map(([k, v]) => (
                                <Bar key={k} label={GATE_LABEL[k] || k} value={v} max={rejectTotal} />
                            ))}
                    </div>
                </Card>
                <Card title="Bottleneck criteria (runs starved here)">
                    <div className="flex flex-col gap-1.5">
                        {summary.bottlenecks.length === 0 && <Empty />}
                        {summary.bottlenecks.map((b) => (
                            <Bar key={b.criterion} label={b.criterion} value={b.count} max={summary.bottlenecks[0]?.count || 1} />
                        ))}
                    </div>
                </Card>
            </div>

            <Card title="Learned contracts (recent versions)">
                {contracts.length === 0 ? (
                    <Empty />
                ) : (
                    <div className="flex flex-col gap-2">
                        {contracts.map((c) => (
                            <div key={c.id} className="rounded-lg border border-gray-100 p-2.5">
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="font-mono text-gray-700">v{c.version}</span>
                                    <span className={`rounded px-1.5 py-0.5 ${c.source === 'edit' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{c.source}</span>
                                    <span>{c.gatedCriteria} gated criteria</span>
                                    <span className="ml-auto">{new Date(c.generatedAt).toLocaleString()}</span>
                                </div>
                                {c.mustPass.length > 0 && (
                                    <div className="mt-1.5 text-xs text-gray-700">
                                        <span className="font-medium text-emerald-700">Must be strong:</span> {c.mustPass.join(' · ')}
                                    </div>
                                )}
                                {c.antiPatterns.length > 0 && (
                                    <div className="mt-0.5 text-xs text-gray-500">
                                        <span className="font-medium text-rose-600">Avoid:</span> {c.antiPatterns.join(' · ')}
                                    </div>
                                )}
                                {c.learnedPreferenceSummary && <div className="mt-1 text-[11px] leading-snug text-gray-500">{c.learnedPreferenceSummary}</div>}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Card title="Recent runs">
                {runs.length === 0 ? (
                    <Empty />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="text-gray-400">
                                <tr className="border-b border-gray-100">
                                    <th className="py-1.5 pr-3 font-medium">When</th>
                                    <th className="py-1.5 pr-3 font-medium">Trigger</th>
                                    <th className="py-1.5 pr-3 font-medium">Status</th>
                                    <th className="py-1.5 pr-3 text-right font-medium">Scanned</th>
                                    <th className="py-1.5 pr-3 text-right font-medium">Scored</th>
                                    <th className="py-1.5 pr-3 text-right font-medium">Delivered</th>
                                    <th className="py-1.5 pr-3 font-medium">Bottleneck</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((r) => (
                                    <tr key={r.id} className="border-b border-gray-50 last:border-0">
                                        <td className="whitespace-nowrap py-2 pr-3 text-gray-500">{new Date(r.createdAt).toLocaleString()}</td>
                                        <td className="py-2 pr-3 text-gray-600">{r.trigger}</td>
                                        <td className="py-2 pr-3">
                                            <span className={`rounded px-1.5 py-0.5 ${r.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : r.status === 'exhausted' ? 'bg-amber-50 text-amber-700' : r.status === 'failed' ? 'bg-rose-50 text-rose-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                                        </td>
                                        <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{num(r.scannedCount)}</td>
                                        <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{num(r.scoredCount)}</td>
                                        <td className="py-2 pr-3 text-right tabular-nums font-medium text-gray-900">{r.deliveredCount}/{r.targetCount}</td>
                                        <td className="py-2 pr-3 text-gray-600">{r.diagnostics?.bottleneckCriterionText || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </>
    );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
            <div className="text-xs font-medium text-gray-500">{label}</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
            {sub && <div className="mt-0.5 text-[11px] text-gray-400">{sub}</div>}
        </div>
    );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-800">{title}</h2>
            {children}
        </div>
    );
}

function Bar({ label, value, max }: { label: string; value: number; max: number }) {
    const w = max ? Math.round((value / max) * 100) : 0;
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className="w-32 shrink-0 truncate text-gray-600">{label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full bg-gray-800" style={{ width: `${w}%` }} />
            </div>
            <span className="w-10 shrink-0 text-right tabular-nums font-medium text-gray-900">{num(value)}</span>
        </div>
    );
}

function Empty() {
    return <div className="py-6 text-center text-xs text-gray-400">No data in this window.</div>;
}
